import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getMe, getStoredUser, getStoredToken, logout as apiLogout } from '../services/api.service';
import { clearStoredScope } from '../constants/scope';
import { hasPermission as checkPermission } from '../utils/permissions';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  role_id?: string;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  logout: () => void;
  setUser: (user: User, token: string) => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  logout: () => { },
  setUser: () => { },
  hasPermission: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(getStoredUser());
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      getMe()
        .then((data) => {
          setUserState(data.user);
          localStorage.setItem('cloud_user', JSON.stringify(data.user));
        })
        .catch(() => {
          apiLogout();
          setUserState(null);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const setUser = (user: User, token: string) => {
    setUserState(user);
    setToken(token);
  };

  const logout = () => {
    setUserState(null);
    setToken(null);
    apiLogout();
    // The selected site is part of the session, not of the browser: leaving it behind would put
    // the next person to log in here inside someone else's site, with that site's menu.
    clearStoredScope();
  };

  // return (
  //   <AuthContext.Provider value={{ user, token, loading, logout, setUser }}>
  //     {children}
  //   </AuthContext.Provider>
  // );

  const hasPermission = (permission: string): boolean => {
    return checkPermission(user, permission);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, logout, setUser, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
